// Library Methods
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { EditIcon, TrashIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import {
  ClinicPackageExamType,
  ClinicPackageType,
} from "src/services/clinics/types";

// Utils
import {
  canEditExamSection,
  getExamText,
  getEyeText,
} from "src/utils/exams/examReportsHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

type ExamPackageDescriptionType = {
  isUpdating: boolean;
  clinicPackage: ClinicPackageType;
  openDeleteModal: () => void;
  openEditDrawer: () => void;
};

const ExamPackageDescription = ({
  isUpdating,
  clinicPackage,
  openDeleteModal,
  openEditDrawer,
}: ExamPackageDescriptionType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, descriptionBackground } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const { dynamicWidth } = useComponentDimensions({
    componentId: "#package-header",
  });

  // check if edit button should be hidden
  const hideEditButton = useCallback(
    (packageContent: ClinicPackageType["content"]) => {
      const parsedPackage: ClinicPackageExamType = JSON.parse(packageContent);
      return canEditExamSection(clinicInfo?.licensing, parsedPackage);
    },
    [clinicInfo?.licensing]
  );

  // get the package and eye descriptions
  const getFormattedContent = useCallback(
    (clinicPackage: ClinicPackageType) => {
      const packageContent = JSON.parse(clinicPackage?.content);
      const packageDescription = getExamText(packageContent, t)
        ?.split("\n")
        ?.filter((content) => Boolean(content.length));
      const eyeDescription = getEyeText(packageContent, t)
        ?.split("\n")
        ?.filter((content) => Boolean(content.length));

      return packageDescription.map(
        (packageDescription: string, index: number) => ({
          packageDescription,
          eyeDescription: eyeDescription[index],
        })
      );
    },
    [t]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: descriptionBackground,
        color: onSurface,
        py: "12px",
        px: "16px",
        borderRadius: "4px",
        gap: "4px",
      }}
    >
      <Box
        id="package-header"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Typography
          fontSize="14px"
          fontWeight={600}
          sx={{
            maxWidth: `calc(${dynamicWidth}px - 225px)`,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {clinicPackage?.name}
        </Typography>
        <Box display="flex" gap="8px">
          <Tooltip title={t("Edit Exam Package")}>
            <IconButton
              disabled={isUpdating}
              onClick={openEditDrawer}
              sx={{
                color: onSurface,
                p: 0,
                display: hideEditButton(clinicPackage?.content)
                  ? "none"
                  : "flex",
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("Delete Exam Package")}>
            <IconButton
              disabled={isUpdating}
              data-testid="delete-icon"
              sx={{
                color: onSurface,
                padding: 0,
              }}
              onClick={openDeleteModal}
            >
              <TrashIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" gap="4px">
        {getFormattedContent(clinicPackage)?.map((content, index) => (
          <Box
            key={content.packageDescription}
            display="flex"
            justifyContent="space-between"
            gap="8px"
          >
            <Typography fontSize="12px">
              {index + 1}. {content.packageDescription}
            </Typography>
            <Typography fontSize="12px" textAlign="right">
              {content.eyeDescription}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ExamPackageDescription;
